import React, { useState, useEffect, useRef } from 'react';

interface SpriteProps {
    spritesheet: string;
    frameWidth: number;
    frameHeight: number;
    frameCount: number;
    fps: number;
    className?: string;
    isAnimating?: boolean;
    currentFrame?: number;
    loop?: boolean; // Whether to loop the animation or stop at the last frame
    onComplete?: () => void; // Callback for when a non-looping animation completes
    reversed?: boolean; // Whether the spritesheet frames go right-to-left instead of left-to-right
}

const Sprite = ({
    spritesheet,
    frameWidth,
    frameHeight,
    frameCount,
    fps,
    className = '',
    isAnimating = true,
    currentFrame = 0,
    loop = true,
    onComplete,
    reversed = false
}: SpriteProps) => {
    const [frame, setFrame] = useState(currentFrame);
    const [isComplete, setIsComplete] = useState(false);
    const animationCompleted = useRef(false);

    // Reset animation state when spritesheet changes
    useEffect(() => {
        setFrame(currentFrame);
        setIsComplete(false);
        animationCompleted.current = false;
    }, [spritesheet, currentFrame]);

    useEffect(() => {
        if (!isAnimating || (isComplete && !loop)) return;

        const interval = setInterval(() => {
            setFrame(prevFrame => {
                // If looping or not yet at the last frame, advance to next frame
                if (loop || prevFrame < frameCount - 1) {
                    const nextFrame = loop ? (prevFrame + 1) % frameCount : prevFrame + 1;

                    // Check if we've reached the last frame in a non-looping animation
                    if (!loop && nextFrame === frameCount - 1 && !animationCompleted.current) {
                        animationCompleted.current = true;
                        setIsComplete(true);
                        // Call the completion callback if provided
                        if (onComplete) {
                            setTimeout(onComplete, 1000 / fps); // Wait one frame duration before calling
                        }
                    }

                    return nextFrame;
                }

                // If not looping and at last frame, stay at last frame
                return prevFrame;
            });
        }, 1000 / fps);

        return () => clearInterval(interval);
    }, [isAnimating, frameCount, fps, loop, onComplete, isComplete]);

    // Calculate the background position based on whether the spritesheet is reversed
    const getBackgroundPosition = () => {
        if (reversed) {
            // For reversed spritesheets (right-to-left), start from the right side
            // and move left as frame increases
            const rightOffset = (frameCount - 1 - frame) * frameWidth;
            return `-${rightOffset}px 0px`;
        } else {
            // For normal spritesheets (left-to-right)
            return `-${frame * frameWidth}px 0px`;
        }
    };

    return (
        <div
            className={`sprite ${className}`}
            style={{
                width: `${frameWidth}px`,
                height: `${frameHeight}px`,
                backgroundImage: `url(${spritesheet})`,
                backgroundPosition: getBackgroundPosition(),
                backgroundRepeat: 'no-repeat',
                backgroundSize: `${frameWidth * frameCount}px ${frameHeight}px`,
                imageRendering: 'pixelated'
            }}
        />
    );
};

export default Sprite;
